import { Stack, Center, VStack } from "@chakra-ui/react";
import React, { useState } from "react";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { RiAccountCircleLine, RiLoginBoxLine } from "react-icons/ri";
import CustomAlert from "../CustomUi/CustomAlert";
import FormContainer from "../CustomUi/FormContainer";
import FormNavigationLinks from "../CustomUi/FormNavigationLinks";
import TextInput from "../CustomUi/TextInput";
import AuthPagesHeader from "../CustomUi/AuthPagesHeader";
import CustomButton from "../CustomUi/CustomButton";
import { validateEmail, validateRequired } from "../../services/validation";
import { useFormChanges } from "../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../hooks/useFormFieldHandler";
import useThemeColors from "../../hooks/useThemeColors";

interface ForgotPasswordData {
  email: string;
}

export default function ForgotPasswordForm() {
  const [forgotPasswordData, setForgotPasswordData] =
    useState<ForgotPasswordData>({
      email: "",
    });
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const navigate = useNavigate();
  const { forgotPassword, loading, error } = useAuth();
  const colors = useThemeColors();
  // Define field configurations for form validation
  const fieldConfigs = {
    email: { type: "email" as const, required: true },
  };

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: null,
      currentData: forgotPasswordData,
      isEditMode: false,
    });

  // Use the form field handler hook
  const { handleFieldChange, handleFieldBlur } = useFormFieldHandler({
    setFormState: setForgotPasswordData,
    setTouchedFields,
  });

  // Custom validation for email
  const validateEmailField = (value: string): string => {
    if (!validateRequired(value)) {
      return "Email is required";
    }
    if (!validateEmail(value)) {
      return "Valid email is required";
    }
    return "";
  };

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof ForgotPasswordData;

    // Custom validation for email field
    if (name === "email") {
      return validateEmailField(value);
    }

    return validateField(fieldKey, value);
  };

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      forgotPasswordData[fieldName as keyof ForgotPasswordData]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      forgotPasswordData[fieldName as keyof ForgotPasswordData]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Check if form has changes
    if (!hasChanges) {
      return;
    }

    // Validate all fields
    const errors = validateAllFields();
    if (hasValidationErrors(errors)) {
      return;
    }

    const result = await forgotPassword({ email: forgotPasswordData.email });
    if (result?.succeeded) {
      setSuccess(true);
    }
  };

  return (
    <Center h={"full"} w={"full"}>
      <FormContainer
        heading={!success ? "Forgot Password?" : "Password Reset Link Sent"}
        description={
          !success
            ? "Tell us your email and we will send a link to reset your password"
            : ""
        }
      >
        {success ? (
          <AuthPagesHeader
            heading={""}
            description={
              "To get back into your account, simply check your email for the password reset link. If you don't see it shortly, be sure to check your spam or junk folder, just in case!"
            }
          />
        ) : (
          <>
            {error.length > 0 && (
              <VStack gap={2} mb={4} align={"stretch"}>
                {error.slice(0, 3).map((errorMessage, index) => (
                  <CustomAlert key={index} message={errorMessage} tight />
                ))}
              </VStack>
            )}
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <Stack gap={4}>
                <TextInput
                  invalid={showFieldError("email")}
                  required
                  placeholder="Email *"
                  type="email"
                  name="email"
                  value={forgotPasswordData.email}
                  onChange={handleFieldChange}
                  onBlur={() => handleFieldBlur("email")}
                  error={showFieldError("email") ? getFieldError("email") : ""}
                />
                <CustomButton
                  type="submit"
                  label="Reset Password"
                  disabled={loading || !hasChanges || hasValidationErrorsValue}
                  loading={loading}
                  w={"full"}
                  colorPalette={colors.brand.primary}
                />
              </Stack>
            </form>

            <FormNavigationLinks
              links={[
                {
                  text: "Log In",
                  icon: RiLoginBoxLine,
                  isDisabled: loading,
                  onClick: () => navigate("/auth/login"),
                },
                {
                  text: "Create Account",
                  icon: RiAccountCircleLine,
                  isDisabled: loading,
                  onClick: () => navigate("/auth/register"),
                },
              ]}
            />
          </>
        )}
      </FormContainer>
    </Center>
  );
}
